import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Divider,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import { 
  TrendingDown, 
  TrendingUp, 
  Person, 
  AttachMoney,
  Remove as RemoveIcon
} from '@mui/icons-material';
import api from '../../api'; 
import SalaryProgressBars from '../../components/bars/SalaryProgressBar';

import { API_URL } from '../../config';
import PageHeader from '../../components/headers/Header';

const SalaryBreakdown = ({isEmployee=true}) => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    setLoading(true);

    const selectedApi = isEmployee 
    ? `${API_URL}/payroll/impact/current-month/employee`
    : `${API_URL}/payroll/impact/current-month`;

    api
      .get(selectedApi,{
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`, 
        },
    })
      .then((response) => {
        if (Array.isArray(response.data)) {
          const validData = response.data.filter(row => row !== null && row !== undefined);
          
          const formattedData = validData.map(row => ({
            id: row.payrollId,
            name: row.employee ? `${row.employee.firstName} ${row.employee.lastName}` : 'N/A',
            baseSalary: row.baseSalary ?? 0,
            netSalary: row.netSalary ?? 0,
            deduction: row.deduction ?? 0,
            bonus: row.bonus ?? 0,
          }));
          
          setComponents(formattedData);
        } else {
          console.error('Unexpected API response format:', response.data);
          setError('Invalid data format received from API');
          setComponents([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching salary components:', error);
        setError('Failed to fetch salary components');
        setComponents([]);
        setLoading(false);
      });
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getSalaryChangeIcon = (deduction) => {
    if (deduction > 0) return <TrendingDown color="error" />;
    if (deduction < 0) return <TrendingUp color="success" />;
    return <RemoveIcon color="disabled" />;
  };

  const getSalaryChangeColor = (deduction) => {
    if (deduction > 0) return 'error';
    if (deduction < 0) return 'success';
    return 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading salary data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 , width: '100%', padding:0, margin:0}}>
      <PageHeader
        title="💰 Salary Impact Report"
        subtitle="Current month salary adjustments due to absences and bonuses"
      />

      {components.length === 0 ? (
        <Alert severity="info">
          No salary adjustments found for this month.
        </Alert>
      ) : (
        <Grid container spacing={3} style={{ width: '100%', m:'auto' }}>
          {components.map((component) => (
            <Grid item xs={12} md={6} lg={4} key={component.id} style={{ width: '31%' }}>
              <Card 
                elevation={3} 
                sx={{ 
                  height: '100%',
                  width: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* Employee Name */}
                  <Box display="flex" alignItems="center" mb={2}>
                    <Person sx={{ mr: 1, color: '#1565c0' }} />
                    <Typography variant="h6" fontWeight="bold">
                      {component.name}
                    </Typography>
                  </Box>

                  {/* Salary Comparison */}
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Base Salary
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        {formatCurrency(component.baseSalary)} dh
                      </Typography>
                    </Stack>

                    {/* <Divider sx={{ my: 1 }} /> */}
                    <SalaryProgressBars 
                      baseSalary={component.baseSalary} 
                      netSalary={component.netSalary} 
                    />

                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Final Salary
                      </Typography>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold"
                        color={component.netSalary < component.baseSalary ? 'error.main' : 'success.main'}
                      >
                        {formatCurrency(component.netSalary)} dh
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Impact Details */}
                  <Box sx={{ mb: 2 }}>
                    {/* Deduction */}
                    {component.deduction !== 0 && (
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                          {getSalaryChangeIcon(component.deduction)}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {component.deduction > 0 ? 'Deduction' : 'Addition'}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${component.deduction > 0 ? '-' : '+'}${formatCurrency(Math.abs(component.deduction))} dh`}
                          color={getSalaryChangeColor(component.deduction)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    )}

                    {/* Bonus */}
                    {component.bonus !== 0 && (
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box display="flex" alignItems="center" py={2}>
                          <TrendingUp color="success" />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            Bonus
                          </Typography>
                        </Box>
                        <Chip
                          label={`+${formatCurrency(component.bonus)} dh`}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    )}

                    {component.deduction === 0 && component.bonus === 0 && (
                      <Box display="flex" justifyContent="center" py={2}>
                        <Typography variant="body2" color="text.secondary">
                          No adjustments this month
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Net Change Summary */}
                  <Divider sx={{ my: 2 }} />
                  <Box 
                    sx={{ 
                      backgroundColor: component.netSalary < component.baseSalary ? '#ffebee' : '#e8f5e8',
                      p: 2,
                      borderRadius: 1,
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block">
                      Net Change
                    </Typography>
                    <Typography 
                      variant="h6" 
                      fontWeight="bold"
                      color={component.netSalary < component.baseSalary ? 'error.main' : 'success.main'}
                    >
                      {component.netSalary < component.baseSalary ? '-' : '+'}
                      {formatCurrency(Math.abs(component.netSalary - component.baseSalary))} dh
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SalaryBreakdown;
// import React, { useEffect, useState } from 'react';
// import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
// import { styled } from '@mui/material/styles';
// import axios from 'axios';

// // API
// const API_URL = process.env.REACT_APP_AXO_PEOPLE_API_URL;

// const StyledListItem = styled(ListItem)(({ theme }) => ({
//   borderBottom: `1px solid #90caf9`, // primary200
//   '&:hover': { backgroundColor: '#f0f5ff' }, // primaryLight
// }));

// const SalaryBreakdown = () => {
//   const [components, setComponents] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     setLoading(true);
//     axios
//       .get(`${API_URL}/payroll/impact/current-month`)
//       .then((response) => {
//         if (Array.isArray(response.data)) {
//           // Filter out null or undefined entries
//           const validData = response.data.filter(row => row !== null && row !== undefined);
          
//           // Map to desired format, assuming name is derived from employee
//           const formattedData = validData.map(row => ({
//             id: row.payrollId,
//             name: row.employee ? `${row.employee.firstName} ${row.employee.lastName}` : 'N/A',
//             baseSalary: row.baseSalary ?? 0,
//             netSalary: row.netSalary ?? 0,
//             deduction: row.deduction ?? 0,
//             bonus: row.bonus ?? 0,
//           }));
          
//           setComponents(formattedData);
//         } else {
//           console.error('Unexpected API response format:', response.data);
//           setError('Invalid data format received from API');
//           setComponents([]);
//         }
//         setLoading(false);
//       })
//       .catch((error) => {
//         console.error('Error fetching salary components:', error);
//         setError('Failed to fetch salary components');
//         setComponents([]);
//         setLoading(false);
//       });
//   }, []);

//   if (loading) {
//     return <Typography>Loading...</Typography>;
//   }

//   if (error) {
//     return <Typography color="error">{error}</Typography>;
//   }

//   return (
//     <Box sx={{ p: 2, backgroundColor: '#f0f5ff', borderRadius: '8px' }}>
//       <Typography variant="h6" color="#1e88e5" gutterBottom>
//         Salary Components Affected by Absences
//       </Typography>
//       <List>
//         {components.length > 0 ? (
//           components.map((component) => (
//             <StyledListItem key={component.id}>
//               <ListItemText
//                 primary={component.name}
//                 secondary={`Old Salary: ${component.baseSalary.toFixed(2)} dh | New Salary: ${component.netSalary.toFixed(2)} dh | Impact: ${component.deduction.toFixed(2)} dh | Bonus: ${component.bonus.toFixed(2)} dh`}
//                 primaryTypographyProps={{ color: '#3d52a0' }}
//                 secondaryTypographyProps={{ color: '#1565c0' }}
//               />
//             </StyledListItem>
//           ))
//         ) : (
//           <StyledListItem>
//             <ListItemText primary="No data available" primaryTypographyProps={{ color: '#3d52a0' }} />
//           </StyledListItem>
//         )}
//       </List>
//     </Box>
//   );
// };

// export default SalaryBreakdown;
// // import React, { useEffect, useState } from 'react';
// // import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
// // import { styled } from '@mui/material/styles';
// // import axios from 'axios';

// // const StyledListItem = styled(ListItem)(({ theme }) => ({
// //   borderBottom: `1px solid #90caf9`, // primary200
// //   '&:hover': { backgroundColor: '#f0f5ff' }, // primaryLight
// // }));

// // const SalaryBreakdown = ({ employeeId='' }) => {
// //   const [components, setComponents] = useState([]);

// //   useEffect(() => {
// //     axios.get('http://localhost:8081/api/payroll/impact/current-month')
// //       .then(response => setComponents(response.data))
// //       .catch(error => console.error('Error fetching salary components:', error));
// //   }, [employeeId]);

// //   return (
// //     <Box sx={{ p: 2, backgroundColor: '#f0f5ff', borderRadius: '8px' }}> {/* primaryLight */}
// //       <Typography variant="h6" color="#1e88e5" gutterBottom> {/* primaryDark */}
// //         Salary Components Affected by Absences
// //       </Typography>
// //       <List>
// //         {components.map((component) => (
// //           <StyledListItem key={component.id}>
// //             <ListItemText
// //               primary={component.name}
// //               secondary={`Amount: ${component.baseSalary.toFixed(2)} dh | Impact: ${component.deduction.toFixed(2)} dh | Bonus: ${component.bonus.toFixed(2)} dh` }
// //               primaryTypographyProps={{ color: '#3d52a0' }} // secondaryMain
// //               secondaryTypographyProps={{ color: '#1565c0' }} // primary800
// //             />
// //           </StyledListItem>
// //         ))}
// //       </List>
// //     </Box>
// //   );
// // };

// // export default SalaryBreakdown;